import { useState, useEffect } from "react";
import SectionWrapper from "@/components/base/SectionWrapper";
import { supabase, type GalleryImage } from "@/lib/supabase";

export default function GalleryPage() {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      const { data } = await supabase.from("gallery").select("*").order("date_uploaded", { ascending: false });
      setGalleryImages(data || []);
      setIsLoading(false);
    };
    fetchData();
  }, []);

  const openLightbox = (index: number) => { setActiveIndex(index); setLightboxOpen(true); document.body.style.overflow = "hidden"; };
  const closeLightbox = () => { setLightboxOpen(false); document.body.style.overflow = ""; };
  const nextImage = () => { setActiveIndex((prev) => (prev + 1) % galleryImages.length); };
  const prevImage = () => { setActiveIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length); };

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-50"><div className="w-10 h-10 border-2 border-primary-900 border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div>
      <section className="relative h-[280px] md:h-[350px] flex items-center justify-center overflow-hidden bg-primary-900">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 2px 2px, white 1px, transparent 0)", backgroundSize: "32px 32px" }} />
        <div className="relative z-10 text-center px-4">
          <p className="text-gold-400 text-xs font-medium uppercase tracking-[2px] mb-3">Memories</p>
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-white">Photo Gallery</h1>
        </div>
      </section>

      <SectionWrapper className="py-12 md:py-20 px-4 md:px-8 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-3">
            {galleryImages.map((img, index) => (
              <button key={img.id} onClick={() => openLightbox(index)} className="relative group overflow-hidden rounded-xl aspect-square cursor-pointer">
                <img src={img.image_url} alt={img.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                <div className="absolute inset-0 bg-primary-900/0 group-hover:bg-primary-900/50 transition-all duration-300 flex flex-col items-center justify-center p-4">
                  <p className="text-white font-heading font-semibold text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-center">{img.title}</p>
                  <p className="text-white/80 text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-300 mt-1">{img.event_name}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </SectionWrapper>

      {lightboxOpen && galleryImages[activeIndex] && (
        <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center" onClick={closeLightbox}>
          <button onClick={closeLightbox} className="absolute top-4 right-4 md:top-6 md:right-6 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-all z-10" aria-label="Close lightbox"><i className="ri-close-line text-xl" /></button>
          <button onClick={(e) => { e.stopPropagation(); prevImage(); }} className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-all z-10" aria-label="Previous image"><i className="ri-arrow-left-s-line text-xl" /></button>
          <button onClick={(e) => { e.stopPropagation(); nextImage(); }} className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-all z-10" aria-label="Next image"><i className="ri-arrow-right-s-line text-xl" /></button>
          <div className="max-w-[90vw] max-h-[85vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img src={galleryImages[activeIndex].image_url} alt={galleryImages[activeIndex].title} className="max-w-full max-h-[70vh] object-contain rounded-lg" />
            <div className="mt-4 text-center">
              <p className="text-white font-heading font-semibold text-lg">{galleryImages[activeIndex].title}</p>
              <p className="text-white/60 text-sm mt-1">{galleryImages[activeIndex].event_name} &middot; {new Date(galleryImages[activeIndex].date_uploaded).toLocaleDateString("en-US", { month: "long", year: "numeric" })}</p>
            </div>
          </div>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10">
            {galleryImages.map((_, i) => (<button key={i} onClick={(e) => { e.stopPropagation(); setActiveIndex(i); }} className={`w-2 h-2 rounded-full transition-all ${i === activeIndex ? "bg-gold-500 w-5" : "bg-white/40"}`} aria-label={`Go to image ${i + 1}`} />))}
          </div>
        </div>
      )}
    </div>
  );
}